<template>
  <div>
    <DataTable :value="rewardsToDisplay">
      <Column header="Name" style="width: 18%">
        <template #body="slotProps">
          <div>
            <span>{{ slotProps.data.name }}</span>
            <span style="display: block; color: #909298">{{ slotProps.data.technicalName }}</span>
          </div>
        </template>
      </Column>
      <Column header="Sprite" style="width: 32%">
        <template #body="slotProps">
          <SpriteSelector
            :options="sprites"
            :store-item-id="slotProps.data.id"
            :model-value="slotProps.data.sprite"
            :upload-in-progress="uploadSpriteLoading"
            no-border
            @upload="handleUploadSprite"
            @link="handleLinkSprite"
            @delete="handleDeleteSprite"
            @unlink-sprite="handleUnLinkSprite"
          ></SpriteSelector>
        </template>
      </Column>
      <Column header="Items" style="width: 30%">
        <template #body="slotProps">
          <ItemsSection
            :items="slotProps.data.items"
            :dropdown-items="dropdownItems"
            :reward-id="slotProps.data.id"
            @update-items="updateRewardItems"
            @remove-item="confirmRemoveRewardItem"
          />
        </template>
      </Column>
      <Column header="Actions" style="width: 20%">
        <template #body="slotProps">
          <DropDownMenu :items="getItems(slotProps.data.id)" />
        </template>
      </Column>
      <template v-if="isWIP" #footer>
        <RewardForm :sprites="sprites" @add-reward="handleAddReward" />
      </template>
    </DataTable>
    <div v-if="rewardsToDisplay.length === 0 && !queryLoading">No rewards available.</div>
  </div>
  <DuplicateDialog
    :is-visible="isDuplicateDialogVisible"
    :selected-reward="rewardToDuplicate || undefined"
    @update:is-visible="(value) => (isDuplicateDialogVisible = value)"
    @cancel="(value) => closeDuplicateDialog(value)"
  />
</template>

<script setup lang="ts">
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { useConfirm } from "primevue/useconfirm";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import DropDownMenu from "@/components/DropDownMenu";
import SpriteSelector from "@/components/SpriteSelector";
import { useGetItemsNameAndSpriteQuery } from "@/graphql";
import useSprites from "@/modules/Items/useSprites";
import DuplicateDialog from "./components/DuplicateDialog.vue";
import ItemsSection from "./components/ItemsSection.vue";
import RewardForm from "./components/RewardForm.vue";
import useRewards from "./hooks/useRewards";
import { DropdownItem, Item, Reward } from "./types";

const route = useRoute();
const appId = route.params.gameId as string;
const versionId = ref<string>(route.params.versionId as string);
const spriteType = "REWARD";

const isWIP = computed(() => versionId.value === "wip");
const { rewardsToDisplay, queryLoading, editReward, fetchRewards, addReward, deleteReward } = useRewards(
  appId,
  versionId.value
);

const {
  sprites: rawSprites,
  uploadSprite,
  deleteSprite,
  uploadSpriteLoading,
  reFetchSprites,
} = useSprites(appId, versionId.value, spriteType);

const sprites = computed(() =>
  rawSprites.value.filter((sprite): sprite is { id: string; name: string; imageUrl: string } => !!sprite)
);

const { result: itemsResult } = useGetItemsNameAndSpriteQuery({ gameId: appId, versionId: versionId.value });
const dropdownItems = computed(() => (itemsResult.value?.getStoreItems || []) as DropdownItem[]);

const handleUploadSprite = async (event: { files: File[] }) => {
  await uploadSprite(event.files, spriteType);
};

const handleDeleteSprite = async (id: string) => {
  await deleteSprite(id);
  await reFetchSprites();
};

const handleLinkSprite = async (rewardId: string, id: string) => {
  await editReward(rewardId, { sprite: id });
  await reFetchSprites();
};

const handleUnLinkSprite = async (rewardId: string) => {
  await editReward(rewardId, { sprite: null });
  await reFetchSprites();
};

const updateRewardItems = async ({ rewardId, items }: { rewardId: string; items: Item[] }) => {
  await editReward(rewardId, { items });
};

const confirm = useConfirm();

const confirmRemoveRewardItem = ({ rewardId, items }: { rewardId: string; items: Item[] }) => {
  confirm.require({
    message: "Are you sure you want to delete this item from the reward?",
    header: "Confirmation",
    accept: async () => {
      await updateRewardItems({ rewardId, items });
    },
  });
};

const handleAddReward = async (newReward: Reward) => {
  await addReward(newReward);
};

const handleDeleteItem = async (id: string) => {
  const selectedReward = rewardsToDisplay.value.find((reward) => reward.id === id);
  if (!selectedReward) {
    return;
  }
  confirm.require({
    message: `You will delete the reward ${selectedReward.name}. Are you sure you want to continue?`,
    header: "Confirmation",
    rejectClass: "p-button-secondary p-button-outlined",
    acceptClass: "p-button-danger",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    accept: async () => {
      await deleteReward(id);
    },
  });
};

const isDuplicateDialogVisible = ref(false);
const rewardToDuplicate = ref(null);

const openDuplicateDialog = (reward: any) => {
  rewardToDuplicate.value = reward;
  isDuplicateDialogVisible.value = true;
};

const closeDuplicateDialog = (refetch = false) => {
  isDuplicateDialogVisible.value = false;

  if (refetch) {
    fetchRewards();
  }
};

const getItems = (id: string) => [
  {
    label: "Duplicate",
    icon: "pi pi-copy",
    command: async () => {
      const item = rewardsToDisplay.value.find((reward) => reward.id === id);
      openDuplicateDialog(item);
    },
    disabled: !isWIP.value,
  },
  {
    label: "Delete",
    icon: "pi pi-trash",
    command: async () => {
      await handleDeleteItem(id);
    },
    disabled: !isWIP.value,
  },
];

onMounted(() => {
  fetchRewards();
});
</script>

<style module>
.headerDescription {
  color: var(--vd-color-text-light-2);
  margin-top: 0.5rem;
}

.rewardsDisplay {
  display: flex;
  flex-direction: column;
}

.rewardItem {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.itemContent {
  display: flex;
  align-items: center;
}

.spriteImage {
  width: 20px;
  height: 20px;
  margin-right: 0.5rem;
}

.itemName {
  font-size: 0.875rem;
  margin-right: 0.5rem;
  color: #333;
}

.quantity {
  font-size: 0.875rem;
  color: #333;
}

.nameContainer {
  display: flex;
  flex-direction: column;
}

.technicalName {
  color: var(--vd-color-text-light-2);
}
</style>
